import { useEffect } from "react";
import { create } from "zustand";

import { EMERALD_NOTE_SEEN_KEY } from "@/lib/emeraldNote";
import { useAuthState } from "@/zustand/AuthState";
import { safeStorage } from "@/utils/safeStorage";

/**
 * Foreign persistence keys this store reads. They are written by the owning
 * components (WebPushPopUp, InstallAppDialog) — keep in sync; centralizing
 * them here at least makes the coupling visible and greppable.
 */
const EMERALD_QUEUE_KEYS = {
  pushDontRemind: "webPushDontRemind",
  pushLastShown: "webPushLastShown",
  installDismissed: "installAppDialogDismissed",
} as const;

/**
 * One promotional/informational dialog per app boot.
 *
 * The app accumulated several self-opening dialogs (Robert's theme note,
 * web-push opt-in, install-app, re-add-to-home-screen hint). Individually
 * each is reasonable; together a user could hit 3+ popups in one session.
 * This store picks ONE winner per boot by fixed priority; everything else
 * stays silent until a later boot (each dialog keeps its own persistence,
 * so nothing is lost — only deferred).
 *
 * Security/consent gates (TC gate, version gate, recovery-key wizard) are
 * NOT announcements and never go through this store. Two deliberate
 * exceptions to "one per boot": the install dialog keeps its pre-login
 * behavior on auth pages (onboarding-critical, only dialog there), and
 * explicitly user-invoked dialogs (AccountMenu's "Install app") bypass
 * the queue entirely — user actions are not announcements.
 */
export type AnnouncementId =
  | "emerald-note"
  | "readd-hint"
  | "web-push"
  | "install-app";

/** Highest priority first. */
const PRIORITY: AnnouncementId[] = [
  "emerald-note",
  "readd-hint",
  "web-push",
  "install-app",
];

export const READD_HINT_SEEN_KEY = "readdHomeScreenHintSeen";
export const READD_HINT_SNOOZE_KEY = "readdHomeScreenHintSnoozeUntil";

/**
 * Throw-safe environment probe. Deliberately NOT environmentDetector: that
 * module computes IS_PWA via window.matchMedia at import time and throws in
 * environments without it, which would poison every import chain that
 * touches announcements. Overridable seam for unit tests.
 */
export const envProbe = {
  get(): { ios: boolean; pwa: boolean } {
    try {
      const nav = navigator as Navigator & { standalone?: boolean };
      const ios =
        /iPad|iPhone|iPod/.test(nav.userAgent) ||
        (nav.platform === "MacIntel" && nav.maxTouchPoints > 1);
      const pwa =
        nav.standalone === true ||
        (typeof window.matchMedia === "function" &&
          window.matchMedia("(display-mode: standalone)").matches);
      return { ios, pwa };
    } catch {
      return { ios: false, pwa: false };
    }
  },
};

/**
 * Legacy-webclip probe: iOS bakes the status-bar style into the webclip at
 * Add-to-Home-Screen time. Installs made before the black-translucent change
 * keep an opaque status bar, so inside them env(safe-area-inset-top) is 0
 * (the webview never extends under the clock). Fresh installs report ~59px
 * (notched) / ~20px (non-notched).
 */
function isLegacyWebclip(): boolean {
  const { ios, pwa } = envProbe.get();
  if (!ios || !pwa) {
    return false;
  }
  try {
    // In landscape the top inset is legitimately 0 on notched iPhones (the
    // notch moves to the sides) — a fresh install would read as "legacy"
    // and we'd tell the user to pointlessly re-add the app (dual-review).
    // Only trust the probe in portrait; other boots resolve a later day.
    if (
      typeof window.matchMedia === "function" &&
      !window.matchMedia("(orientation: portrait)").matches
    ) {
      return false;
    }
    const el = document.createElement("div");
    el.style.cssText =
      "position:fixed;top:0;padding-top:env(safe-area-inset-top,0px);visibility:hidden;pointer-events:none";
    document.body.appendChild(el);
    const inset = parseFloat(getComputedStyle(el).paddingTop) || 0;
    el.remove();
    return inset === 0;
  } catch {
    return false;
  }
}

function asNumber(raw: string | null): number {
  const n = Number(raw ?? 0);
  return Number.isFinite(n) ? n : 0;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Cheap, synchronous eligibility per announcement — localStorage/environment
 * only. Components still apply their own runtime conditions (login state,
 * push-subscription status, install prompt availability) before opening; a
 * winner that turns out ineligible at runtime simply shows nothing this boot.
 */
function computeEligibility(): Record<AnnouncementId, boolean> {
  const env = envProbe.get();
  const pushDontRemind =
    safeStorage.get(EMERALD_QUEUE_KEYS.pushDontRemind) === "true";
  const pushLastShown = asNumber(
    safeStorage.get(EMERALD_QUEUE_KEYS.pushLastShown),
  );
  const installDismissedAt = asNumber(
    safeStorage.get(EMERALD_QUEUE_KEYS.installDismissed),
  );
  const readdSnoozeUntil = asNumber(safeStorage.get(READD_HINT_SNOOZE_KEY));

  // Already-subscribed users must not win the web-push slot: the popup
  // would early-return on isSubscribed and silently burn the boot's
  // announcement (dual-review HIGH). useWebPush persists the preference
  // per-user; read it via the same key scheme.
  const username = useAuthState.getState().username;
  const pushSubscribed =
    !!username &&
    safeStorage.get(`${username}-webPush-userPreference`) === "true";

  return {
    "emerald-note": safeStorage.get(EMERALD_NOTE_SEEN_KEY) !== "true",
    "readd-hint":
      isLegacyWebclip() &&
      safeStorage.get(READD_HINT_SEEN_KEY) !== "true" &&
      Date.now() > readdSnoozeUntil,
    "web-push":
      !pushDontRemind &&
      !pushSubscribed &&
      !(env.ios && !env.pwa) &&
      Date.now() - pushLastShown > DAY_MS,
    "install-app": Date.now() - installDismissedAt > DAY_MS,
  };
}

type AnnouncementState = {
  resolved: boolean;
  winner: AnnouncementId | null;
  resolve: () => void;
};

export const useAnnouncementState = create<AnnouncementState>((set, get) => ({
  resolved: false,
  winner: null,
  resolve: () => {
    if (get().resolved) {
      return;
    }
    const eligible = computeEligibility();
    const winner = PRIORITY.find((id) => eligible[id]) ?? null;
    set({ resolved: true, winner });
  },
}));

/**
 * True when this announcement holds the boot's single announcement slot.
 * The winner is decided once, on the first mounted consumer that is
 * actually participating. Pass `enabled: false` while a consumer's own
 * gate (e.g. login) hasn't opened yet — a disabled consumer neither
 * triggers resolution nor claims the slot, so globally-mounted dialogs
 * on pre-login routes can't lock the winner in early (Copilot review).
 */
export function useAnnouncementSlot(
  id: AnnouncementId,
  enabled = true,
): boolean {
  const resolved = useAnnouncementState((state) => state.resolved);
  const winner = useAnnouncementState((state) => state.winner);
  useEffect(() => {
    if (enabled && !resolved) {
      useAnnouncementState.getState().resolve();
    }
  }, [enabled, resolved]);
  return enabled && resolved && winner === id;
}
